/**
 * Internet -> radio gateway with suppression.
 *
 * A phone that has the room over the internet AND a radio on the room's
 * channel forwards messages from internet-only people onto the air, so the
 * radio side of the room hears them too.
 *
 * The rules that keep this from flooding a shared channel:
 *  - Only FRESH messages (author timestamp within FRESH_MS): joining a room
 *    syncs its whole history, and none of that may be re-radiated.
 *  - Never anything that came from the radio (no echo loops).
 *  - Each gateway waits a staggered delay, derived from the message and the
 *    gateway so gateways rarely pick the same moment; if the message's tag is
 *    heard on air meanwhile (from its author or another gateway), it stands
 *    down. Within radio range one copy goes out; separate areas get one each.
 *  - A tag heard once is remembered for SEEN_MS, so a late duplicate offer
 *    is ignored too.
 */

import type { MessageEntry } from '../sync/crdt'
import { isRadioOrigin, messageTag } from './room-text'

export const FRESH_MS = 90_000
export const SEEN_MS = 10 * 60_000
export const BASE_DELAY_MS = 1500
export const DELAY_STEP_MS = 1250
export const DELAY_SLOTS = 8

export interface GatewayClock {
  now(): number
  setTimeout(fn: () => void, ms: number): unknown
  clearTimeout(handle: unknown): void
}

const realClock: GatewayClock = {
  now: () => Date.now(),
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  clearTimeout: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
}

function fnv1a(text: string): number {
  let h = 0x811c9dc5
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i)
    h = Math.imul(h, 0x01000193) >>> 0
  }
  return h
}

/** Staggered wait before forwarding: differs per gateway, fixed per message. */
export function gatewayDelay(messageId: string, gatewayKey: string): number {
  return BASE_DELAY_MS + (fnv1a(`${messageId}|${gatewayKey}`) % DELAY_SLOTS) * DELAY_STEP_MS
}

export class RadioGateway {
  private pending = new Map<string, unknown>()
  private seen = new Map<string, number>()

  constructor(
    private readonly opts: {
      /** Put this entry on air (the session formats, paces and confirms). */
      forward: (entry: MessageEntry) => Promise<unknown>
      /** Stable per-gateway key, e.g. the radio's public key. */
      gatewayKey: string
      clock?: GatewayClock
    }
  ) {}

  private get clock(): GatewayClock {
    return this.opts.clock ?? realClock
  }

  /** A message just arrived from the internet. Maybe forward it. */
  offer(entry: MessageEntry): 'scheduled' | 'skipped' {
    const now = this.clock.now()
    this.expire(now)
    if (isRadioOrigin(entry)) return 'skipped'
    if (entry.type === 'system') return 'skipped'
    if (Math.abs(now - entry.timestamp) > FRESH_MS) return 'skipped'
    const tag = messageTag(entry.id)
    if (this.seen.has(tag) || this.pending.has(tag)) return 'skipped'
    const handle = this.clock.setTimeout(() => {
      this.pending.delete(tag)
      if (this.seen.has(tag)) return
      this.seen.set(tag, this.clock.now())
      void this.opts.forward(entry).catch(() => undefined)
    }, gatewayDelay(entry.id, this.opts.gatewayKey))
    this.pending.set(tag, handle)
    return 'scheduled'
  }

  /** A tagged frame was heard on air (or sent by this phone): stand down. */
  heard(tag: string): void {
    this.seen.set(tag, this.clock.now())
    const handle = this.pending.get(tag)
    if (handle !== undefined) {
      this.clock.clearTimeout(handle)
      this.pending.delete(tag)
    }
  }

  get pendingCount(): number {
    return this.pending.size
  }

  dispose(): void {
    for (const h of this.pending.values()) this.clock.clearTimeout(h)
    this.pending.clear()
  }

  private expire(now: number): void {
    for (const [tag, at] of this.seen) if (now - at > SEEN_MS) this.seen.delete(tag)
  }
}
